import { useEffect, useMemo, useState } from 'react';
import { coachDayKey as getCoachDayKey } from './coachDayKey';

/** Latest-message reads are independent of bulk activity/social hydration. */
export default function useCoachHomePreview({ backendService, hasAiFeature, enabled, timeZone }) {
  const [, setClockDay] = useState(null);
  // Compute on render as well as at the boundary: cached snapshots and a
  // resumed tab must never authorize yesterday's message for a new local day.
  const coachDayKey = getCoachDayKey(Date.now(), timeZone);
  useEffect(() => {
    if (!enabled) return undefined;
    let timer;
    const tick = () => {
      setClockDay(getCoachDayKey(Date.now(), timeZone));
      clearTimeout(timer);
      timer = setTimeout(tick, 60000 - (Date.now() % 60000));
    };
    tick();
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [enabled, timeZone]);
  const scope = useMemo(() => ({ backendService, hasAiFeature, enabled }), [backendService, hasAiFeature, enabled]);
  const [snapshot, setSnapshot] = useState(null);
  const available = Boolean(hasAiFeature && backendService?.fetchCoachHome);

  useEffect(() => {
    if (!enabled || !available) return undefined;
    let active = true;
    let latestRequest = 0;
    let home = null;
    // Invalidate even a previously visited scope (A -> B -> A).
    setSnapshot({ scope, home: null, settled: false });
    const refresh = async () => {
      const requestId = ++latestRequest;
      let payload = null;
      try {
        payload = await backendService.fetchCoachHome();
      } catch {
        // BackendService normally returns null on failure; alternate transports
        // may reject. Both must release the initial wait and preserve refreshes.
      }
      if (!active || requestId !== latestRequest) return;
      // A failed refresh retains already-canonical prose, while a successful
      // empty snapshot is authoritative and allows the daily fallback again.
      if (payload) home = payload;
      setSnapshot({ scope, home, settled: true });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'hidden') refresh();
    };
    refresh();
    window.addEventListener('coach-timeline-updated', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const interval = setInterval(refreshWhenVisible, 30000);
    return () => {
      active = false;
      window.removeEventListener('coach-timeline-updated', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      clearInterval(interval);
    };
  }, [scope, backendService, enabled, available]);

  // Mask a previous service/feature scope synchronously, before effects run.
  return {
    coachDayKey,
    coachHomePreview: snapshot?.scope === scope ? snapshot.home : null,
    coachHomePreviewSettled: enabled && (!available || (snapshot?.scope === scope && snapshot.settled)),
  };
}
